export type BridgeLifecycleMode = "revocable" | "leased" | "persistent";
export type BridgeFootprint = readonly [number, number, number, number];

export interface BridgeEvent {
  readonly time: number;
  readonly from: number;
  readonly to: number;
  readonly mode: BridgeLifecycleMode;
  readonly duration?: number;
  readonly label: string;
}

export interface BridgeTimelinePoint {
  readonly time: number;
  readonly kappa: number;
  readonly footprint: BridgeFootprint;
}

export interface BridgeTimeline {
  readonly maxKappa: number;
  readonly points: readonly BridgeTimelinePoint[];
}

export interface BridgeExposureWindow {
  readonly endTime: number;
  readonly startPercent: number;
  readonly widthPercent: number;
}

export const BRIDGE_LIFECYCLE_EVENTS: readonly BridgeEvent[] = [
  { time: 2, from: 0, to: 1, mode: "revocable", label: "Auth check" },
  {
    time: 4,
    from: 1,
    to: 2,
    mode: "leased",
    duration: 6,
    label: "Data pipeline",
  },
  { time: 7, from: 0, to: 3, mode: "persistent", label: "Budget grant" },
  { time: 12, from: 2, to: 3, mode: "revocable", label: "Temp audit" },
];

export const BRIDGE_LIFECYCLE_TOTAL_TIME = 18;

export function bridgeFootprintKappa(footprint: BridgeFootprint): number {
  return (
    2 *
    (footprint[0] * footprint[1] +
      footprint[0] * footprint[2] +
      footprint[0] * footprint[3] +
      footprint[1] * footprint[2] +
      footprint[1] * footprint[3] +
      footprint[2] * footprint[3])
  );
}

function eventsByTime(
  events: readonly BridgeEvent[],
): ReadonlyMap<number, readonly BridgeEvent[]> {
  const byTime = new Map<number, BridgeEvent[]>();
  for (const event of events) {
    const bucket = byTime.get(event.time) ?? [];
    bucket.push(event);
    byTime.set(event.time, bucket);
  }
  return byTime;
}

function mutateFootprint(
  footprint: number[],
  axis: number,
  delta: 1 | -1,
): void {
  if (axis === 0 || axis === 1 || axis === 2 || axis === 3) {
    footprint[axis] = Math.max(0, (footprint[axis] ?? 0) + delta);
  }
}

export function bridgeEventExpiry(event: BridgeEvent): number | null {
  if (event.mode === "revocable") return event.time + 1;
  if (event.mode === "leased") return event.time + (event.duration ?? 5);
  return null;
}

export function bridgeExposureWindow(
  event: BridgeEvent,
  totalTime: number = BRIDGE_LIFECYCLE_TOTAL_TIME,
): BridgeExposureWindow {
  const endTime = bridgeEventExpiry(event) ?? totalTime;
  return {
    endTime,
    startPercent: (event.time / totalTime) * 100,
    widthPercent: ((endTime - event.time) / totalTime) * 100,
  };
}

export function buildBridgeTimeline(
  events: readonly BridgeEvent[],
  totalTime: number = BRIDGE_LIFECYCLE_TOTAL_TIME,
): BridgeTimeline {
  const schedule = eventsByTime(events);
  const active: { expiry: number | null; from: number; to: number }[] = [];
  const footprint = [0, 0, 0, 0];
  const points: BridgeTimelinePoint[] = [];
  let maxKappa = 1;

  for (let time = 0; time <= totalTime; time += 1) {
    let kept = 0;
    for (const entry of active) {
      if (entry.expiry !== null && entry.expiry <= time) {
        mutateFootprint(footprint, entry.from, -1);
        if (entry.from !== entry.to) mutateFootprint(footprint, entry.to, -1);
        continue;
      }
      active[kept] = entry;
      kept += 1;
    }
    active.length = kept;

    for (const event of schedule.get(time) ?? []) {
      mutateFootprint(footprint, event.from, 1);
      if (event.from !== event.to) mutateFootprint(footprint, event.to, 1);
      active.push({
        expiry: bridgeEventExpiry(event),
        from: event.from,
        to: event.to,
      });
    }

    const kappa = bridgeFootprintKappa([
      footprint[0] ?? 0,
      footprint[1] ?? 0,
      footprint[2] ?? 0,
      footprint[3] ?? 0,
    ]);
    maxKappa = Math.max(maxKappa, kappa);
    points.push({
      time,
      kappa,
      footprint: [
        footprint[0] ?? 0,
        footprint[1] ?? 0,
        footprint[2] ?? 0,
        footprint[3] ?? 0,
      ],
    });
  }

  return { maxKappa, points };
}

export function bridgeModeCount(
  events: readonly BridgeEvent[],
  mode: BridgeLifecycleMode,
): number {
  return events.filter((event) => event.mode === mode).length;
}

export function hasPersistentBridge(events: readonly BridgeEvent[]): boolean {
  return events.some((event) => event.mode === "persistent");
}
